import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { PermissionsHttpClient } from '@omnea-app/permissions/permissionsHttpClient'
import { RequestDetailQueryKey, RequestDetailsQueryKey } from '../../useRequestDetail'
import { GetRequestRecordPermissionsQueryKey } from './useRequestRecordPermissions'

export const useUpdateRequestConfidentiality = (requestId: string) => {
  const { getToken } = useAuthenticatedUser()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation('requests')

  return useMutation({
    mutationFn: async (isConfidential: boolean) => {
      const authorization = await getToken()
      const permissionsClient = new PermissionsHttpClient({ authorization })
      const { data, error } = await permissionsClient.POST('/request-access/confidentiality', {
        body: { requestId, isConfidential },
      })
      throwIfApiError(error)
      return data
    },
    onSuccess: async () => {
      // The detail response carries rawData.isConfidential, which drives both the toggle label and
      // the header indicator — refetch it so the flip is visible without a reload. The record
      // permissions refetch too: marking can revoke the marker's own admin access (self-lockout),
      // and a 5-minute-stale answer would keep offering an unmark the endpoint will reject.
      await Promise.all(
        [[RequestDetailQueryKey, requestId], [RequestDetailsQueryKey], [GetRequestRecordPermissionsQueryKey]].map((queryKey) =>
          queryClient.invalidateQueries({ queryKey })
        )
      )
    },
    onError: () => {
      // The menu and confirmation close on submit, so without this a failed flip is silent.
      enqueueSnackbar(t('confidentiality.update-failed'), { variant: 'error' })
    },
  })
}
